import React, { useState } from 'react';
import { t } from 'i18next';
import { string, func, arrayOf, shape, number, object, oneOfType, bool } from 'prop-types';
import { useSelector } from 'react-redux';

import { COUNTRY_CONFIRM_FLOW } from '~screens/Dashboard/components/ChangeAddress/constants';
import withSpinner from '~components/Spinner';
import ErrorMessage from '~components/ErrorMessage';
import { ERROR_IMAGES } from '~components/ErrorMessage/constants';
import Tabs from '~components/Tabs';
import storePropTypes from '~constants/propTypes';
import { googleAddressPropTypes } from '~propTypes/address';
import LocalStorageService from '~services/LocalStorageService';
import { isMethodConsistent } from '~utils/dispatchMethod';
import { DISPATCH_METHODS } from '~constants/address';
import { coreSettingsSelector } from '~screens/Dashboard/screens/Home/redux/selectors';
import { GO_TO_CHECKOUT_FROM_ADDRESS } from '~redux/SearchStore/constants';
import { CURRENT_COUNTRY_ID } from '~constants/environment';

import Delivery from './components/Delivery';
import PickUp from './components/PickUp';
import { TABS_DATA } from './constants';
import styles from './styles.module.scss';

// eslint-disable-next-line complexity
function DeliveryOrPickUp({
  selectedAddress,
  selectedDeliveryTime,
  delivery,
  deliveryError,
  stores,
  storesError,
  selectedStore,
  onStoreSelect,
  onConfirm,
  onDispatchChange,
  changeTab,
  onCleanSearchInput,
  createUserAddress,
  editUserAddress,
  setSelectedDeliveryTime,
  onAddressSelect,
  onSearchInputChange,
  incompleteAddress,
  onMarkerDragEnd,
  loadingStores,
  scheduled,
  setScheduled
}) {
  const [automaticTabChange, setAutomaticTabChange] = useState(false);
  const fakeStoreId = LocalStorageService.getFakeStoreId();
  const pickupStores = stores?.length;
  const searchStore = useSelector(state => state.searchStore);
  const coreSettings = useSelector(coreSettingsSelector);
  const userAddresses = useSelector(state => state.searchStore.userAddresses);
  const currentUser = useSelector(state => state.auth?.currentUser);
  const isCallCenter = !!useSelector(state => state.auth?.callCenterGuestUser);
  const withConfirmAddressFlow = COUNTRY_CONFIRM_FLOW.includes(CURRENT_COUNTRY_ID);
  const [isConfirmAddress, setConfirmAddress] = useState(Boolean(selectedAddress?.placeId));
  const undoConfirmAddress = () => setConfirmAddress(false);
  const onConfirmAddress = () => setConfirmAddress(true);

  const deliveryStores = Object.keys(delivery || {});
  const futureOrdersEnabled =
    coreSettings.futureOrdersEnabled && isMethodConsistent(delivery.orderMethod, searchStore.dispatchType);

  const tabs = TABS_DATA.map(tab => {
    if (tab.value === DISPATCH_METHODS.PJ_DELIVERY && deliveryError && selectedAddress) {
      changeTab(1);
      return { ...tab, disabled: true };
    }
    return tab;
  });

  if (
    (tabs.length === 1 || searchStore?.dispatchType === DISPATCH_METHODS.IN_STORE) &&
    !automaticTabChange &&
    !isCallCenter
  ) {
    changeTab(1);
    setAutomaticTabChange(true);
  }

  return (
    <div className={`column center full-width ${styles.container}`}>
      {!pickupStores && !deliveryStores ? (
        <ErrorMessage
          className="item-1"
          img={ERROR_IMAGES.noPickup}
          title={fakeStoreId && !storesError ? t('DeliveryOrPickUp:pickStore') : t('DeliveryOrPickUp:noData')}
          message={
            fakeStoreId && !storesError
              ? t('DeliveryOrPickUp:pickStoreMessage')
              : t('DeliveryOrPickUp:noDataMessage')
          }
        />
      ) : (
        <Tabs tabData={tabs} tabBarClassName={styles.tabContainer}>
          <Delivery
            className={styles.contentBody}
            error={deliveryError}
            selectedAddress={selectedAddress}
            futureOrdersEnabled={futureOrdersEnabled}
            selectedDeliveryTime={selectedDeliveryTime}
            deliveryInfo={delivery}
            onDispatchChange={onDispatchChange}
            onConfirm={onConfirm}
            changeTab={changeTab}
            goToCheckout={searchStore[GO_TO_CHECKOUT_FROM_ADDRESS]}
            userAddresses={userAddresses}
            createUserAddress={createUserAddress}
            editUserAddress={editUserAddress}
            logged={!!currentUser}
            isConfirmAddress={isConfirmAddress}
            setSelectedDeliveryTime={setSelectedDeliveryTime}
            searchStore={searchStore}
            withConfirmAddressFlow={withConfirmAddressFlow}
            onAddressSelect={onAddressSelect}
            onSearchInputChange={onSearchInputChange}
            incompleteAddress={incompleteAddress}
            onMarkerDragEnd={onMarkerDragEnd}
            undoConfirmAddress={undoConfirmAddress}
            onConfirmAddress={onConfirmAddress}
            loadingStores={loadingStores}
            scheduled={scheduled}
            setScheduled={setScheduled}
          />

          <PickUp
            className={styles.contentBody}
            selectedAddress={selectedAddress}
            stores={stores}
            error={storesError}
            selectedStore={selectedStore}
            onStoreSelect={onStoreSelect}
            onDispatchChange={onDispatchChange}
            onConfirm={onConfirm}
            changeTab={changeTab}
            deliveryError={deliveryError}
            onCleanSearchInput={onCleanSearchInput}
            isConfirmAddress={isConfirmAddress}
            searchStore={searchStore}
            selectedDeliveryTime={selectedDeliveryTime}
            setSelectedDeliveryTime={setSelectedDeliveryTime}
            onAddressSelect={onAddressSelect}
            onSearchInputChange={onSearchInputChange}
            incompleteAddress={incompleteAddress}
            loadingStores={loadingStores}
            undoConfirmAddress={undoConfirmAddress}
            scheduled={scheduled}
            setScheduled={setScheduled}
          />
        </Tabs>
      )}
    </div>
  );
}

DeliveryOrPickUp.propTypes = {
  onAddressSelect: func.isRequired,
  onConfirm: func.isRequired,
  onStoreSelect: func.isRequired,
  changeTab: func,
  createUserAddress: func,
  delivery: shape({
    name: string,
    deliveryWaitTime: number,
    menuId: number
  }),
  deliveryError: string,
  editUserAddress: func,
  incompleteAddress: oneOfType([string, undefined]),
  loadingStores: bool,
  scheduled: bool,
  selectedAddress: googleAddressPropTypes,
  selectedDeliveryTime: object,
  selectedStore: storePropTypes,
  setScheduled: func,
  setSelectedDeliveryTime: func,
  stores: arrayOf(storePropTypes),
  storesError: string,
  onCleanSearchInput: func,
  onDispatchChange: func,
  onMarkerDragEnd: func,
  onSearchInputChange: func
};

export default withSpinner({
  WrappedComponent: DeliveryOrPickUp,
  className: styles.spinner,
  delay: 700,
  simple: true
});
